import { RADIUS } from "@elyxeer/ui";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import FormHelperText from "@mui/material/FormHelperText";
import FormLabel from "@mui/material/FormLabel";
import { useTranslation } from "react-i18next";

import {
  CONTENT_TOPICS,
  type ContentTopic,
  MAX_CONTENT_TOPICS,
} from "~/types/news-admin";

/**
 * The topic selector: the closed vocabulary as toggle chips, not a text field.
 *
 * Topics used to be typed in as a comma-separated list, which is how the corpus
 * ended up with twenty-six of them and how `spanek` and `spánek` could both exist.
 * Offering the list is the whole point of closing the vocabulary — an editor cannot
 * coin a topic the member app has no label or filter for.
 *
 * Toggle chips rather than a multi-select: eight options all fit on screen, the cap
 * needs to be visible while choosing rather than enforced on submit, and an editor
 * should be able to see the whole taxonomy while classifying.
 *
 * Rendered as a fieldset of checkbox-role chips, because that is what it is: a
 * bounded multiple choice, not navigation.
 */
export function TopicPicker({
  value,
  onChange,
  error,
}: {
  value: ContentTopic[];
  onChange: (next: ContentTopic[]) => void;
  /** True once the editor has been shown the requirement and still has it wrong. */
  error: boolean;
}) {
  const { t } = useTranslation();
  const atCap = value.length >= MAX_CONTENT_TOPICS;

  const toggle = (topic: ContentTopic) => {
    if (value.includes(topic)) {
      onChange(value.filter((item) => item !== topic));
      return;
    }
    if (atCap) return;
    // Appended rather than sorted into vocabulary order: the first topic an editor
    // picks is the primary one, and the member app renders them in this order.
    onChange([...value, topic]);
  };

  return (
    <Box component="fieldset" sx={{ border: 0, margin: 0, padding: 0 }}>
      <FormLabel
        component="legend"
        error={error}
        sx={{ typography: "body2", marginBottom: 1 }}
      >
        {t("editor.fields.topics")}
      </FormLabel>

      <Box
        data-testid="editor.topics"
        sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}
      >
        {CONTENT_TOPICS.map((topic) => {
          const selected = value.includes(topic);

          return (
            <Chip
              key={topic}
              label={t(`topics.${topic}`)}
              onClick={() => toggle(topic)}
              role="checkbox"
              aria-checked={selected}
              color={selected ? "primary" : "default"}
              variant={selected ? "filled" : "outlined"}
              // Disabled rather than silently ignored: an editor at the cap should be
              // told to deselect something, not left tapping a chip that does nothing.
              disabled={!selected && atCap}
              data-testid={`editor.topic-${topic}`}
              sx={{ borderRadius: `${RADIUS.full}px` }}
            />
          );
        })}
      </Box>

      <FormHelperText error={error} data-testid="editor.topics-help">
        {error
          ? t("editor.fields.topicsRequired")
          : t("editor.fields.topicsHelper", {
              selected: value.length,
              max: MAX_CONTENT_TOPICS,
            })}
      </FormHelperText>
    </Box>
  );
}
